import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractPdfTextInBrowser } from "@/lib/extractPdfText";
import { renderAndUploadPdfPages } from "@/lib/pdfPageRenderer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Trash2, RefreshCw, ExternalLink, Eye, FileSearch, ImagePlus } from "lucide-react";

interface Material {
  id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  summary: string | null;
  text_content: string | null;
  extracted_images: unknown[] | null;
  created_at: string;
}

interface MaterialsListProps {
  projectId: string;
}

const MaterialsList = ({ projectId }: MaterialsListProps) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [viewContentMaterial, setViewContentMaterial] = useState<Material | null>(null);
  const { toast } = useToast();

  const handleView = async (material: Material) => {
    if (material.file_type === "url") {
      const url = material.storage_path;
      if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast({ variant: "destructive", title: "Invalid URL", description: "This material has no valid URL." });
      }
    } else {
      try {
        const { data, error } = await supabase.storage
          .from("materials")
          .createSignedUrl(material.storage_path, 3600);
        if (error) throw error;
        if (data?.signedUrl) {
          window.open(data.signedUrl, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Cannot open file",
          description: err instanceof Error ? err.message : "Failed to load file",
        });
      }
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [projectId]);

  // Listen for material updates
  useEffect(() => {
    const handleMaterialUpdate = () => {
      fetchMaterials();
    };

    window.addEventListener("materials-updated", handleMaterialUpdate);
    return () => {
      window.removeEventListener("materials-updated", handleMaterialUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("materials")
      .select("*")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error loading materials",
        description: error.message,
      });
    } else {
      setMaterials(data || []);
    }
    setLoading(false);
  };

  const isPdfMaterial = (m: Material) =>
    (m.file_name || "").toLowerCase().endsWith(".pdf") || m.file_type === "application/pdf";

  const handleProcess = async (material: Material) => {
    const materialId = material.id;
    const storagePath = material.storage_path;
    setProcessingIds(prev => new Set(prev).add(materialId));

    let clientExtractedText: string | null = null;
    const isStoredFile = !storagePath.startsWith("http://") && !storagePath.startsWith("https://");
    if (isPdfMaterial(material) && isStoredFile) {
      try {
        const { data: blob, error: downloadError } = await supabase.storage
          .from("materials")
          .download(storagePath);
        if (!downloadError && blob) {
          clientExtractedText = await extractPdfTextInBrowser(await blob.arrayBuffer());
          if (clientExtractedText) console.log("Extracted PDF text in browser:", clientExtractedText.length, "chars");
        }
      } catch (e) {
        console.warn("Client PDF extraction failed, will use server:", e);
      }
    }

    try {
      const body: { materialId: string; storagePath: string; clientExtractedText?: string } = {
        materialId,
        storagePath,
      };
      if (clientExtractedText) body.clientExtractedText = clientExtractedText;

      const { data, error } = await supabase.functions.invoke("process-material", { body });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const chars = (data as { debug?: { extractedLength?: number } })?.debug?.extractedLength;
      toast({
        title: "Success",
        description: chars != null && chars > 0
          ? `Material added to course. Extracted ${chars.toLocaleString()} characters — use "Extracted content" to view.`
          : "Material added to course successfully",
      });
      
      // Refresh materials to update UI and hide the "Add to Course" button
      await fetchMaterials();
    } catch (error) {
      console.error('Processing error:', error);
      toast({
        variant: "destructive",
        title: "Error adding material to course",
        description: error instanceof Error ? error.message : "Failed to add material",
      });
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(materialId);
        return next;
      });
    }
  };

  const handleProcessAll = async () => {
    const unprocessedMaterials = materials.filter(m => !m.summary || !m.text_content);
    
    if (unprocessedMaterials.length === 0) {
      toast({
        title: "All materials processed",
        description: "All materials have already been added to the course.",
      });
      return;
    }

    // Process all unprocessed materials
    for (const material of unprocessedMaterials) {
      setProcessingIds(prev => new Set(prev).add(material.id));
    }

    let successCount = 0;
    let errorCount = 0;

    try {
      // Process all materials in parallel for better performance
      const processingPromises = unprocessedMaterials.map(async (material) => {
        let clientExtractedText: string | null = null;
        const isPdf = (material.file_name || "").toLowerCase().endsWith(".pdf") || material.file_type === "application/pdf";
        const isStoredFile = !material.storage_path.startsWith("http://") && !material.storage_path.startsWith("https://");
        if (isPdf && isStoredFile) {
          try {
            const { data: blob, error: downloadError } = await supabase.storage.from("materials").download(material.storage_path);
            if (!downloadError && blob) clientExtractedText = await extractPdfTextInBrowser(await blob.arrayBuffer());
          } catch {
            /* use server extraction */
          }
        }
        const body: { materialId: string; storagePath: string; clientExtractedText?: string } = {
          materialId: material.id,
          storagePath: material.storage_path,
        };
        if (clientExtractedText) body.clientExtractedText = clientExtractedText;
        try {
          const { data, error } = await supabase.functions.invoke("process-material", { body });

          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          
          return { success: true, materialId: material.id };
        } catch (error) {
          console.error(`Error processing material ${material.id}:`, error);
          return { success: false, materialId: material.id };
        } finally {
          setProcessingIds(prev => {
            const next = new Set(prev);
            next.delete(material.id);
            return next;
          });
        }
      });

      const results = await Promise.all(processingPromises);
      successCount = results.filter(r => r.success).length;
      errorCount = results.filter(r => !r.success).length;

      // Refresh materials from database to get updated state (summary and text_content)
      // This ensures the "Add to Course" buttons disappear for processed materials
      await fetchMaterials();

      if (errorCount > 0) {
        toast({
          variant: "destructive",
          title: "Partially completed",
          description: `${successCount} materials added successfully, ${errorCount} failed.`,
        });
      } else {
        toast({
          title: "Success",
          description: `All ${successCount} materials added to course successfully`,
        });
      }
    } catch (error) {
      console.error('Process all error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add materials",
      });
      // Still try to refresh materials even if there was an error
      await fetchMaterials();
    }
  };

  const handleDelete = async (materialId: string) => {
    if (!confirm("Are you sure you want to delete this material?")) return;

    const { error } = await supabase
      .from("materials")
      .delete()
      .eq("id", materialId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error deleting material",
        description: error.message,
      });
    } else {
      toast({
        title: "Success",
        description: "Material deleted successfully",
      });
      fetchMaterials();
    }
  };

  const [imageProgress, setImageProgress] = useState<{ id: string; current: number; total: number } | null>(null);

  const handleExtractImages = async (material: Material) => {
    setProcessingIds((prev) => new Set(prev).add(material.id));
    try {
      // Step 1: Render all PDF pages as images client-side (handles all encoding types)
      toast({ title: "Rendering pages…", description: `Preparing ${material.file_name} for image extraction` });
      const pageRenders = await renderAndUploadPdfPages(
        material.storage_path,
        material.id,
        material.file_type,
        (current, total) => setImageProgress({ id: material.id, current, total }),
      );
      setImageProgress(null);

      if (pageRenders.length === 0) {
        toast({ title: "No pages rendered", description: "Could not render any pages from this PDF." });
        return;
      }

      // Step 2: Send page render URLs to server for Gemini figure identification
      toast({ title: "Identifying figures…", description: `Analyzing ${pageRenders.length} pages with AI` });
      const { data, error } = await supabase.functions.invoke("process-material", {
        body: {
          materialId: material.id,
          storagePath: material.storage_path,
          imageExtractionOnly: true,
          pageRenderUrls: pageRenders,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const count = data?.extractedImages ?? 0;
      toast({
        title: count > 0 ? "Images extracted" : "No figures found",
        description:
          count > 0
            ? `Found ${count} figure${count !== 1 ? "s" : ""} in ${material.file_name}`
            : `No significant figures/diagrams found in ${material.file_name}`,
      });
      await fetchMaterials();
    } catch (err) {
      console.error("Image extraction error:", err);
      toast({
        variant: "destructive",
        title: "Image extraction failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setImageProgress(null);
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(material.id);
        return next;
      });
    }
  };

  const handleExtractAllImages = async () => {
    const pdfMaterials = materials.filter((m) => {
      const ext = (m.file_name.split(".").pop() || "").toLowerCase();
      return (ext === "pdf" || (m.file_type || "").includes("pdf")) && !m.extracted_images;
    });
    if (pdfMaterials.length === 0) {
      toast({ title: "Nothing to extract", description: "All PDFs already have images extracted (or no PDFs found)." });
      return;
    }
    for (const m of pdfMaterials) {
      await handleExtractImages(m);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground">Loading materials...</p>;
  }

  if (materials.length === 0) {
    return (
      <p className="text-muted-foreground">
        No materials uploaded yet. Click "Upload Material" to get started.
      </p>
    );
  }

  const unprocessedCount = materials.filter(m => !m.summary || !m.text_content).length;
  const isProcessingAny = processingIds.size > 0;
  const pdfsWithoutImages = materials.filter((m) => {
    const ext = (m.file_name.split(".").pop() || "").toLowerCase();
    return (ext === "pdf" || (m.file_type || "").includes("pdf")) && m.summary && !m.extracted_images;
  });

  return (
    <div className="space-y-3">
      {pdfsWithoutImages.length > 0 && (
        <div className="flex justify-between items-center mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-sm text-muted-foreground">
            <ImagePlus className="h-4 w-4 inline mr-1" />
            {pdfsWithoutImages.length} PDF{pdfsWithoutImages.length !== 1 ? "s" : ""} can have images extracted for richer lesson content
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExtractAllImages}
            disabled={isProcessingAny}
          >
            <ImagePlus className={`h-4 w-4 mr-2`} />
            Extract images from all PDFs
          </Button>
        </div>
      )}
      {unprocessedCount > 0 && (
        <div className="flex justify-between items-center mb-4 p-3 bg-muted rounded-lg">
          <div className="text-sm text-muted-foreground">
            {unprocessedCount} material{unprocessedCount !== 1 ? 's' : ''} not yet added to course
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleProcessAll}
            disabled={isProcessingAny}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isProcessingAny ? 'animate-spin' : ''}`} />
            {isProcessingAny ? "Adding..." : "Add All to Course"}
          </Button>
        </div>
      )}
      {materials.map((material) => {
        const isProcessing = processingIds.has(material.id);
        const needsProcessing = !material.summary || !material.text_content;
        
        return (
          <div
            key={material.id}
            className="p-4 border rounded-lg hover:bg-accent transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <FileText className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium mb-1">{material.file_name}</p>
                  <p className="text-sm text-muted-foreground mb-2">{material.file_type}</p>
                  {material.file_type === "url" && material.storage_path && (
                    <a
                      href={material.storage_path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1 mb-2 break-all"
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      {material.storage_path}
                    </a>
                  )}
                  {material.summary ? (
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {material.summary}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      Not added to course yet - click "Add to course" to extract content
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewContentMaterial(material)}
                  title="View extracted text (debug)"
                >
                  <FileSearch className="h-4 w-4 mr-1" />
                  Extracted content
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(material)}
                  title={material.file_type === "url" ? "Open URL" : "View file"}
                >
                  {material.file_type === "url" ? (
                    <ExternalLink className="h-4 w-4 mr-1" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1" />
                  )}
                  {material.file_type === "url" ? "Open URL" : "View"}
                </Button>
                {needsProcessing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleProcess(material)}
                    disabled={isProcessing}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${isProcessing ? 'animate-spin' : ''}`} />
                    {isProcessing ? "Adding..." : "Add to Course"}
                  </Button>
                )}
                {(() => {
                  const ext = (material.file_name.split(".").pop() || "").toLowerCase();
                  const isPdf = ext === "pdf" || (material.file_type || "").includes("pdf");
                  const hasImages = Array.isArray(material.extracted_images) && material.extracted_images.length > 0;
                  const progress = imageProgress?.id === material.id ? imageProgress : null;
                  if (isPdf && material.summary) {
                    return (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExtractImages(material)}
                        disabled={isProcessing}
                        title={hasImages ? `${material.extracted_images!.length} figures extracted — click to re-extract` : "Extract figures from this PDF"}
                      >
                        <ImagePlus className={`h-4 w-4 mr-1 ${isProcessing ? "animate-spin" : ""}`} />
                        {progress
                          ? `Rendering ${progress.current}/${progress.total}…`
                          : isProcessing
                            ? "Identifying figures…"
                            : hasImages
                              ? `${material.extracted_images!.length} figures`
                              : "Extract figures"}
                      </Button>
                    );
                  }
                  return null;
                })()}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(material.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      <Dialog open={!!viewContentMaterial} onOpenChange={(open) => !open && setViewContentMaterial(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Extracted content {viewContentMaterial ? `— ${viewContentMaterial.file_name}` : ""}
            </DialogTitle>
            <DialogDescription>
              {viewContentMaterial?.text_content != null ? (
                <span>{viewContentMaterial.text_content.length.toLocaleString()} characters stored</span>
              ) : (
                "No text extracted yet. Add to course to run extraction."
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0 rounded-md border p-3 text-sm font-mono whitespace-pre-wrap break-words">
            {viewContentMaterial?.text_content ?? "(empty)"}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialsList;
